// ABOUTME: React Query mutation hooks for creating, updating, and deleting pump settings configurations.
// ABOUTME: Transforms form values to API request shape and invalidates queries on success.

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '../../../../App.store'
import {
  PumpSettingsApiResponse,
  PumpSettingsFormValues,
  PutPumpSettingsRequest,
} from '../types/pumpSettings'
import { toCreateScopeAndConfigRequest } from '../utils/pendingScope'
import type { PendingScopeRequest } from '../context/EntityFormContext'

export const toApiRequest = (values: PumpSettingsFormValues): PutPumpSettingsRequest => ({
  availablePumps: values.availablePumps
    ? {
      isOverridden: true,
      value: {
        pumpSizeUomId: values.availablePumps.pumpSizeUomId,
        defaultPumpCode: values.availablePumps.defaultPumpCode,
        pumps: values.availablePumps.pumps.map((p) => ({
          code: p.code,
          // Validation in PumpSettingsForm ensures size > 0 before submit; ?? 0 is a safe fallback
          size: p.size ?? 0,
          minLeadTime: p.minLeadTime ?? 0,
          leadTimeUomId: p.leadTimeUomId,
          referenceEvent: p.referenceEvent ?? '',
        })),
      },
    }
    : { isOverridden: false, value: null },
  hoseLength: values.hoseLength
    ? {
      uomId: values.hoseLength.uomId,
      default: values.hoseLength.default,
      min: values.hoseLength.min,
      max: values.hoseLength.max,
      step: values.hoseLength.step,
    }
    : undefined,
  defaultAnswers: values.defaultAnswers
    ? {
      canLargerPumpFit: values.defaultAnswers.canLargerPumpFit,
      isPossibleToCleanPumpOnSite: values.defaultAnswers.isPossibleToCleanPumpOnSite,
      isExtraHoseNeeded: values.defaultAnswers.isExtraHoseNeeded,
      defaultDischarge: values.defaultAnswers.defaultDischarge,
    }
    : undefined,
})

export const useCreatePumpSettings = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()
  const normalizedBusinessLine = businessLine?.toUpperCase()

  return useMutation({
    mutationFn: async ({ scopeId, values }: { scopeId: string; values: PumpSettingsFormValues }) => {
      const res = await api.post<PumpSettingsApiResponse | undefined>(
        `/tuc-configs/pump-configurations/${scopeId}`,
        toApiRequest(values)
      )
      return res.data
    },
    onSuccess: (data, { scopeId }) => {
      queryClient.setQueriesData<PumpSettingsApiResponse[]>(
        {
          queryKey: ['pumpSettingsConfig', countryId, normalizedBusinessLine],
          // Full key shape is ['pumpSettingsConfig', countryId, businessLine, scopeId, rootScopeId].
          predicate: (query) => query.queryKey[3] === scopeId,
        },
        (old) => {
          if (!old || !data) return old
          const idx = old.findIndex((r) => r.scopeId === scopeId)
          return idx === -1 ? [...old, data] : old.map((r) => (r.scopeId === scopeId ? data : r))
        }
      )
      queryClient.invalidateQueries({ queryKey: ['pumpSettingsConfig', countryId, normalizedBusinessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'pumpConfiguration', countryId, businessLine] })
    },
  })
}

export const useCreatePumpSettingsWithScope = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()
  const normalizedBusinessLine = businessLine?.toUpperCase()

  return useMutation({
    mutationFn: async ({ scope, values }: { scope: PendingScopeRequest; values: PumpSettingsFormValues }) => {
      const res = await api.post<PumpSettingsApiResponse | undefined>(
        '/tuc-configs/pump-configurations',
        toCreateScopeAndConfigRequest(scope, toApiRequest(values))
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pumpSettingsConfig', countryId, normalizedBusinessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'pumpConfiguration', countryId, businessLine] })
    },
  })
}

export const useUpdatePumpSettings = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()
  const normalizedBusinessLine = businessLine?.toUpperCase()

  return useMutation({
    mutationFn: async ({ scopeId, values }: { scopeId: string; values: PumpSettingsFormValues }) => {
      const res = await api.put<PumpSettingsApiResponse | undefined>(
        `/tuc-configs/pump-configurations/${scopeId}`,
        toApiRequest(values)
      )
      return res.data
    },
    onSuccess: (data, { scopeId }) => {
      queryClient.setQueriesData<PumpSettingsApiResponse[]>(
        {
          queryKey: ['pumpSettingsConfig', countryId, normalizedBusinessLine],
          // Full key shape is ['pumpSettingsConfig', countryId, businessLine, scopeId, rootScopeId].
          predicate: (query) => query.queryKey[3] === scopeId,
        },
        (old) => {
          if (!old || !data) return old
          const idx = old.findIndex((r) => r.scopeId === scopeId)
          return idx === -1 ? old : old.map((r) => (r.scopeId === scopeId ? data : r))
        }
      )
      queryClient.invalidateQueries({ queryKey: ['pumpSettingsConfig', countryId, normalizedBusinessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'pumpConfiguration', countryId, businessLine] })
    },
  })
}

export const useDeletePumpSettings = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()
  const normalizedBusinessLine = businessLine?.toUpperCase()

  return useMutation({
    mutationFn: async (scopeId: string) => {
      await api.delete(`/tuc-configs/pump-configurations/${scopeId}`)
    },
    onSuccess: (_data, scopeId) => {
      queryClient.setQueriesData<PumpSettingsApiResponse[]>(
        { queryKey: ['pumpSettingsConfig', countryId, normalizedBusinessLine] },
        (old) => old ? old.filter((r) => r.scopeId !== scopeId) : old
      )
      queryClient.invalidateQueries({ queryKey: ['pumpSettingsConfig', countryId, normalizedBusinessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'pumpConfiguration', countryId, businessLine] })
    },
  })
}
